import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import { useNavigate, Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import { GetData, PostData } from "../../services/api";
import Swal from 'sweetalert2';

const CreateLead = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingContactLists, setLoadingContactLists] = useState(true);
  const [contactLists, setContactLists] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    address: "",
    contactListId: "",
  });
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    // Obtener información del usuario desde localStorage
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const user = JSON.parse(authUser);
        setUserInfo({
          id: user.id || user._id,
          companyId: user.companyId,
        });
      } catch (error) {
        console.error("Error parsing authUser:", error);
      }
    }

    // Cargar listas de contactos
    fetchContactLists();
  }, []);

  const fetchContactLists = async () => {
    try {
      setLoadingContactLists(true);
      const response = await GetData("/contact-lists");
      if (response && response.data) {
        setContactLists(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching contact lists:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron cargar las listas de contactos",
        icon: "error",
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setLoadingContactLists(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      Swal.fire({
        title: "Campo requerido",
        text: "El nombre es obligatorio",
        icon: "warning",
        confirmButtonColor: "#E9553E",
      });
      return false;
    }
    if (!formData.lastName.trim()) {
      Swal.fire({
        title: "Campo requerido",
        text: "El apellido es obligatorio",
        icon: "warning",
        confirmButtonColor: "#E9553E",
      });
      return false;
    }
    if (!formData.email.trim()) {
      Swal.fire({
        title: "Campo requerido",
        text: "El email es obligatorio",
        icon: "warning",
        confirmButtonColor: "#E9553E",
      });
      return false;
    }
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      Swal.fire({
        title: "Email inválido",
        text: "Por favor ingresa un email válido",
        icon: "warning",
        confirmButtonColor: "#E9553E",
      });
      return false;
    }
    if (!formData.phone.trim()) {
      Swal.fire({
        title: "Campo requerido",
        text: "El teléfono es obligatorio",
        icon: "warning",
        confirmButtonColor: "#E9553E",
      });
      return false;
    }
    if (!formData.contactListId) {
      Swal.fire({
        title: "Campo requerido",
        text: "Debes seleccionar una lista de contactos",
        icon: "warning",
        confirmButtonColor: "#E9553E",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!userInfo || !userInfo.id || !userInfo.companyId) {
      Swal.fire({
        title: "Error",
        text: "No se pudo obtener la información del usuario. Por favor, inicia sesión nuevamente.",
        icon: "error",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    try {
      setLoading(true);

      // Preparar los datos del lead
      const leadData = {
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        city: formData.city.trim() || undefined,
        address: formData.address.trim() || undefined,
      };

      // Construir la URL del endpoint
      // POST /customers/{company}/{userId}/{createdBy}/{contactList}
      const endpoint = `/customers/${userInfo.companyId}/${userInfo.id}/${userInfo.id}/${formData.contactListId}`;

      const response = await PostData(endpoint, leadData);

      if (response && response.status === 200) {
        Swal.fire({
          title: "¡Éxito!",
          text: "Lead creado correctamente",
          icon: "success",
          confirmButtonColor: "#E9553E",
          confirmButtonText: "Aceptar"
        }).then(() => {
          // Redirigir a la lista de leads
          navigate("/leads");
        });
      }
    } catch (error) {
      console.error("Error creating lead:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Error al crear el lead. Por favor, intenta de nuevo.",
        icon: "error",
        confirmButtonColor: "#E9553E",
        confirmButtonText: "Aceptar"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs
          title="IHubG"
          breadcrumbItem={
            <>
              <Link to="/leads" style={{ color: "inherit", textDecoration: "none" }}>
                Leads
              </Link>{" "}
              / Nuevo Lead
            </>
          }
        />

        <Row>
          <Col lg={8} className="mx-auto">
            <Card>
              <CardHeader>
                <h5 className="mb-0">
                  <i className="ri-user-add-line me-2"></i>
                  Crear Nuevo Lead
                </h5>
              </CardHeader>
              <CardBody>
                {loadingContactLists ? (
                  <Loader message="Cargando listas de contactos..." />
                ) : (
                  <form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Label className="form-label">
                          Nombre <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Nombre"
                          required
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Label className="form-label">
                          Apellidos <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Apellidos"
                          required
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Label className="form-label">
                          Email <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="email@ejemplo.com"
                          required
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Label className="form-label">
                          Teléfono <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1234567890"
                          required
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Label className="form-label">Ciudad</Label>
                        <Input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Ciudad"
                        />
                      </Col>
                      <Col md={6} className="mb-3">
                        <Label className="form-label">Dirección</Label>
                        <Input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Dirección"
                        />
                      </Col>
                      <Col md={12} className="mb-3">
                        <Label className="form-label">
                          Lista de Contactos <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          name="contactListId"
                          value={formData.contactListId}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Selecciona una lista</option>
                          {contactLists.map((list) => (
                            <option key={list.id} value={list.id}>
                              {list.name} {list.type === 'dynamic' ? '(Dinámica)' : '(Estática)'}
                            </option>
                          ))}
                        </Input>
                        {contactLists.length === 0 && (
                          <small className="text-muted">
                            No hay listas de contactos disponibles.{" "}
                            <Link to="/contact-lists">Crear una lista</Link>
                          </small>
                        )}
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <Link to="/leads">
                        <Button color="secondary" outline>
                          <i className="ri-close-line me-1"></i>
                          Cancelar
                        </Button>
                      </Link>
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading || contactLists.length === 0}
                        style={{ backgroundColor: "#E9553E", borderColor: "#E9553E" }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creando...
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1"></i>
                            Crear Lead
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CreateLead;

